import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { twJoin } from "tailwind-merge";

import type { ChatDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { ChatReplyPayload } from "@/components/ChatWindow/ChatWindow";
import { ChatWindow } from "@/components/ChatWindow/ChatWindow";
import { useChatReplies } from "@/components/ChatWindow/useChatReplies";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { useChatReplyHandler } from "@/queries/chats/helpers";
import { chatMutations } from "@/queries/chats/mutations";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { EditGroupChatModal } from "./EditGroupChatModal";

export const ChatDetailPage = (): React.ReactNode => {
  const [isEditGroupModalOpened, editGroupModalHandler] = useBool(false);
  const [refChatWindow, setRefChatWindow] = useState<HTMLDivElement | null>(null);

  const { t } = useTranslation();
  const slug = useSlug();
  const [queryParams, setQueryParams] = useSearchParams();
  const { id: chatId } = useParams<{
    id: string;
  }>();
  const chatIdForQuery = chatId ?? "";
  const chatQueries = useChatQueries();
  const {
    data: chatDetails,
    isPending: isLoadingChatDetails,
    error: errorLoadingChatDetails,
  } = useQuery({
    ...chatQueries.chatDetails({
      chatId: chatIdForQuery,
    }),
    enabled: !!chatId,
  });
  const {
    data: repliesData,
    hasNextPage: hasMoreReplies,
    fetchNextPage: fetchMoreReplies,
    isPending: isLoadingReplies,
    isFetchingNextPage: isFetchingMoreReplies,
    error: errorLoadingReplies,
  } = useInfiniteQuery({
    ...chatQueries.listChatRepliesInfinite({
      chatId: chatIdForQuery,
    }),
    enabled: !!chatId,
  });
  const addChatReplyMutation = chatMutations.useAddChatReply();

  const { chatReplies } = useChatReplies({
    chatId,
    repliesData,
    signalREvent: SIGNALR_EVENTS.CHAT.NEW_PROJECT_CHAT_REPLY_RECEIVED,
  });
  const { sendReply } = useChatReplyHandler({ addChatReplyMutation });

  // If the prefilledReply has been consumed, remove it
  useEffect(() => {
    if (queryParams.get("prefilledReply") && refChatWindow) {
      const newParams = new URLSearchParams(queryParams);

      newParams.delete("prefilledReply");
      setQueryParams(newParams, { replace: true });
    }
  }, [queryParams, refChatWindow, setQueryParams]);

  const handleAddChatReply = async ({ message, images, videos }: ChatReplyPayload) => {
    if (!chatId) {
      throw new Error("Cannot send a reply without a chat ID");
    }

    return await sendReply({ message, images, videos, chatId });
  };

  const error = !chatId || errorLoadingChatDetails || errorLoadingReplies;
  if (isLoadingChatDetails || isLoadingReplies) {
    return <FullSizeLoader />;
  }
  if (error) {
    return <ErrorPage error={error} />;
  }

  let chatName: string;
  if (chatDetails.chatType === "single") {
    if (!chatDetails.privateChatInfo) {
      return <ErrorPage status={404} />;
    }
    chatName = chatDetails.privateChatInfo.receiver.fullName;
  } else {
    if (!chatDetails.groupChatInfo) {
      return <ErrorPage status={404} />;
    }
    chatName = chatDetails.groupChatInfo.name;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={chatName}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.chat-detail.breacrumbs.chats"),
              to: routes.chats.list({
                slug,
              }),
            },
            {
              name: chatName,
            },
          ]}
        />
      }
    >
      <div ref={setRefChatWindow}>
        <ChatWindow
          canSendChatReply={chatDetails.canReply}
          chatReplies={chatReplies}
          hasMoreReplies={hasMoreReplies}
          fetchMoreReplies={fetchMoreReplies}
          isLoadingMoreReplies={isFetchingMoreReplies}
          sendReply={handleAddChatReply}
          chatName={chatName}
          renderChatName={(chatName) => (
            <ChatNameBlock title={chatName} chatDetails={chatDetails} onClickEditChat={editGroupModalHandler.setTrue} />
          )}
          prefilledReply={queryParams.get("prefilledReply") ?? undefined}
        />
      </div>

      {chatDetails.groupChatInfo && (
        <EditGroupChatModal
          isOpened={isEditGroupModalOpened}
          onOpenChange={editGroupModalHandler.set}
          chatId={chatId}
          groupInfo={chatDetails.groupChatInfo}
        />
      )}
    </DocumentPage>
  );
};
interface ChatNameBlockProps {
  title: string | undefined;
  chatDetails: ChatDto;
  onClickEditChat: () => void;
}
const ChatNameBlock = ({ title, chatDetails, onClickEditChat }: ChatNameBlockProps) => {
  const isEditChatAvailable = chatDetails?.chatType === "group" && chatDetails.groupChatInfo?.isMember;
  const receiverAvatar = chatDetails.privateChatInfo?.receiver.avatar;

  return (
    <section className="inline-flex items-center gap-space-sm overflow-hidden">
      {chatDetails.chatType === "single" && receiverAvatar && <UserAvatar image={receiverAvatar} />}
      {chatDetails.chatType === "group" && chatDetails.groupChatInfo?.image && (
        <UserAvatar image={chatDetails.groupChatInfo.image} />
      )}
      {chatDetails.chatType === "group" && !chatDetails.groupChatInfo?.image && (
        <span className="flex size-control-md items-center justify-center rounded-full border border-border-default bg-background-default text-text-secondary">
          <Users01Icon size={24} />
        </span>
      )}
      <h1
        data-testid="chat-title"
        className={twJoin(
          "flex-1 truncate text-heading-2",
          isEditChatAvailable && "transition-colors hover:cursor-pointer hover:text-text-brand",
        )}
        onClick={isEditChatAvailable ? onClickEditChat : undefined}
        role={isEditChatAvailable ? "button" : "heading"}
        tabIndex={isEditChatAvailable ? 0 : -1}
      >
        {title}
      </h1>
    </section>
  );
};
